import {
  Button,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  Box
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { format, parse } from 'date-fns';
import { useNavigate, useParams } from 'react-router-dom';
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import CustomSelect from '../../components/CustomSelect';
import CustomInput from '../../components/CustomInput';
import {
  getBankBranchData,
  getBankDropdownData,
  getLoanTypeData,
  getMasterListType,
  saveApiData,
  setBranchDetails,
  getStateData,
  getCityData,
  setCityData
} from '../../store/Slice/Global/GlobalSlice';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { setApiError, setApiSuccess } from '../../store/Slice/Error/ErrorSlice';
import { RevenueApi } from '../../utils/Apis';

const PRIMARY_COLOR = '#13519d';
const BORDER_COLOR = '#e2e8f0';

function FormSectionCard({ title, children }) {
  return (
    <Box sx={{ position: 'relative', width: '100%', mt: 3, mb: 3.5 }}>
      <Box
        sx={{
          borderRadius: '16px',
          border: `1px solid ${BORDER_COLOR}`,
          bgcolor: '#ffffff',
          p: { xs: 2.5, md: 3.5 },
          pt: { xs: 4, md: 4.5 },
          position: 'relative',
          boxShadow: '0 4px 14px rgba(0,0,0,0.03)'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -14,
            left: 20,
            px: 2.5,
            py: 0.6,
            borderRadius: '999px',
            bgcolor: PRIMARY_COLOR,
            color: '#ffffff',
            fontSize: 12,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            boxShadow: '0 2px 8px rgba(19, 81, 157, 0.25)'
          }}
        >
          {title}
        </Box>
        {children}
      </Box>
    </Box>
  );
}

export default function AddEditRevenue() {
  const [userType, setUserType] = useState('Manual');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { rowId } = useParams();

  const { LoanTypeDrop, BankDrop, MasterTypeListDrop, BankBranchDrop, stateDrop, CityDrop } =
    useGlobalSelector();
  const { LoginData } = useLoginSelector();

  const {
    handleSubmit: handleReduxSubmit,
    control,
    setValue,
    watch,
    formState: { errors }
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      bankId: '',
      fileNumber: '',
      borrowerName: '',
      branchId: '',
      loanTypeId: '',
      misMonth: '',
      provinceId: '',
      districtId: ''
    }
  });

  const watchFileTypeId = watch('revenueFileTypeId');
  const fileTypeInputs = {
    128: { name: 'fileRequestDate', label: 'Request Date', type: 'date' },
    130: { name: 'fileApprovalDate', label: 'Approval Date', type: 'date' },
    132: { name: 'fileDisbursedDate', label: 'Disburse Date', type: 'date' }
  };
  const inputProps = fileTypeInputs[watchFileTypeId];

  const loanAmountInput = {
    132: { name: 'loanAmountDisbursed', label: 'Disbursed Amount' },
    default: { name: 'loanAmountRequested', label: 'Total Loan Amount' }
  };
  const inputAmount = loanAmountInput[watchFileTypeId] || loanAmountInput.default;

  const handleBranchDetails = (bankValue) => {
    if (!bankValue) {
      setValue('branchId', null);
      dispatch(setBranchDetails(null));
      return '';
    }
    dispatch(getBankBranchData(bankValue));
  };

  const handleStateChange = (stateVal) => {
    dispatch(setCityData([]));
    setValue('districtId', null);
    if (stateVal) dispatch(getCityData({ stateId: stateVal }));
  };

  const getLoanTypeBankDropAPi = useCallback(() => {
    if (!LoginData) return;
    const data = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId
    };
    dispatch(getLoanTypeData(data));
    dispatch(getBankDropdownData(data));
  }, [LoginData, dispatch]);

  const getMasterTypeListApi = useCallback(() => {
    const type = 'REVENUE_FILE_STATUS_TYPE';
    dispatch(getMasterListType(type));
  }, [dispatch]);

  const getStateDropApi = useCallback(() => {
    dispatch(getStateData({ countryId: 101 }));
  }, [dispatch]);

  useEffect(() => {
    getLoanTypeBankDropAPi();
    getMasterTypeListApi();
    getStateDropApi();
  }, [getLoanTypeBankDropAPi, getMasterTypeListApi, getStateDropApi]);

  const formatToMidnight = (value) => {
    if (!value) return null;
    try {
      return format(new Date(value), "yyyy-MM-dd'T00:00:00'");
    } catch {
      return value;
    }
  };

  const onSubmit = useCallback(
    async (data) => {
      const finalData = {
        ...data,
        companyId: LoginData?.companyId,
        userId: LoginData?.userId,
        leadId: null,
        empClass: 'Others',
        misIsMatch: false,
        fileRequestDate: formatToMidnight(data.fileRequestDate),
        fileApprovalDate: formatToMidnight(data.fileApprovalDate),
        fileDisbursedDate: data.fileDisbursedDate
          ? format(new Date(data.fileDisbursedDate), 'dd-MM-yyyy')
          : null,
        misMonth: data.misMonth
          ? format(parse(data.misMonth, 'yyyy-MM', new Date()), 'dd-MM-yyyy')
          : null
      };
      try {
        const res = await dispatch(saveApiData(RevenueApi?.addNewFile, finalData));
        if (res?.status === 'success') {
          dispatch(setApiSuccess('File saved successfully'));
          navigate('/revenue');
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        dispatch(setApiError('Something went wrong'));
      }
    },
    [LoginData, dispatch, navigate]
  );

  return (
    <Page title="Partner Management System || Revenue">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <PageTitle info={rowId ? 'Edit File' : 'Add New File'} />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>

          <form id="addRevenue" onSubmit={handleReduxSubmit(onSubmit)}>
            {/* Section 1: Mode Selection */}
            <Box sx={{ mb: 2 }}>
              <FormControl>
                <RadioGroup row value={userType} onChange={(e) => setUserType(e.target.value)}>
                  <FormControlLabel
                    value="Manual"
                    control={
                      <Radio
                        size="small"
                        sx={{ color: PRIMARY_COLOR, '&.Mui-checked': { color: PRIMARY_COLOR } }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                        Manual Entry
                      </Typography>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Box>

            {/* Section 2: File & Loan Details */}
            <FormSectionCard title="File & Loan Details">
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomSelect
                    name="bankId"
                    label="Bank Name"
                    control={control}
                    errors={errors}
                    options={BankDrop || []}
                    isRequired
                    onChange={(_, val) => handleBranchDetails(val)}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <CustomSelect
                    name="branchId"
                    label="Branch Name"
                    control={control}
                    errors={errors}
                    options={BankBranchDrop || []}
                    isRequired
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <CustomInput
                    name="fileNumber"
                    label="File No"
                    control={control}
                    errors={errors}
                    isRequired
                    fieldCode="FILE_NO"
                    fieldType="TEXT_FIELD"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <CustomInput
                    name="borrowerName"
                    label="Borrower Name"
                    control={control}
                    errors={errors}
                    isRequired
                    fieldCode="NAME"
                    fieldType="ALPHABET"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <CustomSelect
                    name="revenueFileTypeId"
                    label="File Type"
                    control={control}
                    errors={errors}
                    options={MasterTypeListDrop || []}
                    isRequired
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <CustomSelect
                    name="loanTypeId"
                    label="Loan Product Type"
                    control={control}
                    errors={errors}
                    options={LoanTypeDrop || []}
                    isRequired
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <CustomInput
                    name={inputAmount?.name}
                    label={inputAmount?.label}
                    control={control}
                    errors={errors}
                    isRequired
                    fieldCode="AMOUNT"
                    fieldType="AMOUNT_FIELD"
                    type="text"
                    isAmount
                  />
                </Grid>

                {inputProps && (
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name={inputProps.name}
                      type={inputProps.type}
                      label={inputProps.label}
                      control={control}
                      errors={errors}
                      isRequired
                      fieldCode="DATE"
                      fieldType="DATE_FIELD"
                    />
                  </Grid>
                )}

                <Grid item xs={12} sm={6} md={4}>
                  <CustomInput
                    name="misMonth"
                    label="MIS Month"
                    type="month"
                    control={control}
                    errors={errors}
                    isRequired
                    fieldCode="MONTH"
                    fieldType="DATE_FIELD"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <CustomSelect
                    name="provinceId"
                    label="State"
                    control={control}
                    errors={errors}
                    options={stateDrop || []}
                    isRequired
                    onChange={(_, val) => handleStateChange(val)}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <CustomSelect
                    name="districtId"
                    label="District / City"
                    control={control}
                    errors={errors}
                    options={CityDrop || []}
                    isRequired
                  />
                </Grid>
              </Grid>
            </FormSectionCard>

            {/* Bottom Actions */}
            <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/revenue')}
                sx={{
                  borderRadius: '8px',
                  px: 3.5,
                  py: 1.1,
                  fontWeight: 600,
                  textTransform: 'none',
                  borderColor: '#cbd5e1',
                  color: '#64748b',
                  '&:hover': {
                    borderColor: '#94a3b8',
                    backgroundColor: '#f8fafc'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  backgroundColor: PRIMARY_COLOR,
                  color: '#ffffff',
                  borderRadius: '8px',
                  px: 4.5,
                  py: 1.1,
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(19, 81, 157, 0.24)',
                  textTransform: 'none',
                  fontSize: '14px',
                  '&:hover': { backgroundColor: '#0d3b75' }
                }}
              >
                Submit File
              </Button>
            </Stack>
          </form>
        </Box>
      </Container>
    </Page>
  );
}
